import React, { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import Masonry from 'react-smart-masonry';

import ResourceModalHandler from 'app/components/CommonStyled/ResourceModalHandler';
import { RESOURCE_SUBMISSION_FINISHED_ACTION } from 'app/components/ResourceSubmission';
import { Sparkle } from 'app/components/shared/icons';
import { LayoutGroup } from 'framer-motion';
import { setShowResourceSubmissionModal } from 'store/reducers/resource-submission/resource-submission-reducer';
import { setDrafts } from 'store/reducers/resources/resources-reducer';

import ResourceCard from '../ResourceCard';
import useMyResources from '../hooks/useMyResources';
import {
  ResourcesMyResourcesContainer,
  ResourcesMyResourcesTitle,
  ResourcesMyResourcesTitleContainer,
  ResourcesMyResourcesWrapper,
  ResourcesMyResourcesHeaderWrapper,
  ResourcesMyResourcesCreateNewButton,
  ResourcesMyResourcesCreateNewButtonText,
} from './styled';

const breakpoints = { mobile: 0, tablet: 900, desktop: 1200, large_desktop: 1600 };

export default function ResourcesMyResources() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data, refetch } = useMyResources();

  useEffect(() => {
    if (data?.drafts) {
      dispatch(setDrafts(data.drafts));
    }
  }, [data?.drafts, dispatch]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === RESOURCE_SUBMISSION_FINISHED_ACTION) {
        refetch();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [queryClient]);

  const handleCreateNew = () => {
    dispatch(setShowResourceSubmissionModal(true));
  };

  return (
    <LayoutGroup>
      <ResourcesMyResourcesWrapper>
        {data?.published.length > 0 ? (
          <ResourcesMyResourcesContainer>
            <ResourcesMyResourcesHeaderWrapper>
              <ResourcesMyResourcesTitleContainer>
                <Sparkle width={24} height={24} fill="var(--color-primary)" />
                <ResourcesMyResourcesTitle>My Submissions</ResourcesMyResourcesTitle>
              </ResourcesMyResourcesTitleContainer>
              <ResourcesMyResourcesCreateNewButton onClick={handleCreateNew}>
                <ResourcesMyResourcesCreateNewButtonText>
                  CREATE NEW
                </ResourcesMyResourcesCreateNewButtonText>
              </ResourcesMyResourcesCreateNewButton>
            </ResourcesMyResourcesHeaderWrapper>
            <Masonry
              breakpoints={breakpoints}
              columns={{ mobile: 1, tablet: 2, desktop: 3, large_desktop: 4 }}
              gap={{ mobile: 24, tablet: 24, desktop: 24, large_desktop: 24 }}
              autoArrange
            >
              {data?.published.map((resource) => (
                <ResourceCard
                  key={resource.uuid}
                  resource={{
                    ...resource,
                    title: resource.title,
                    has_paywall: false,
                    tags: [],
                    submitter: resource.submitter,
                    open_in_modal: resource.open_in_modal,
                    contributors: { ...resource.contributors, communities: [] },
                  }}
                  showClients
                />
              ))}
            </Masonry>
          </ResourcesMyResourcesContainer>
        ) : null}
        {data?.drafts.length > 0 ? (
          <ResourcesMyResourcesContainer hasDrafts>
            <ResourcesMyResourcesTitleContainer>
              <Sparkle width={24} height={24} fill="var(--color-primary)" />
              <ResourcesMyResourcesTitle>Unpublished</ResourcesMyResourcesTitle>
            </ResourcesMyResourcesTitleContainer>
            <Masonry
              breakpoints={breakpoints}
              columns={{ mobile: 1, tablet: 2, desktop: 3, large_desktop: 4 }}
              gap={{ mobile: 24, tablet: 24, desktop: 24, large_desktop: 24 }}
              autoArrange
            >
              {data?.drafts.map((resource) => (
                <ResourceCard
                  key={resource.uuid}
                  resource={{
                    ...resource,
                    title: resource.title,
                    has_paywall: false,
                    tags: [],
                    submitter: resource.submitter,
                    open_in_modal: resource.open_in_modal,
                    contributors: { ...resource.contributors, communities: [] },
                  }}
                  showClients
                />
              ))}
            </Masonry>
          </ResourcesMyResourcesContainer>
        ) : null}
      </ResourcesMyResourcesWrapper>
      <ResourceModalHandler />
    </LayoutGroup>
  );
}
